import { Trans, useLingui } from '@lingui/react'
import { type FunctionComponent } from 'react'

import { getPlaceholderImage } from 'shared/utils/imageUtils'
import Image from 'views/components/atoms/Image'
import Price from 'views/components/atoms/Price'
import ImageWithDiscountBadge from 'views/components/molecules/ImageWithDiscountBadge'
import Modal from 'views/components/molecules/Modal'
import Notification, {
  NOTIFICATION_TYPES,
} from 'views/components/molecules/Notification'
import './LifeStyleProductModal.scss'
import RxBonusContent, {
  RXBonusContentHeading,
} from 'views/components/organisms/Cart/RxBonusContent'
import TransWithHtml from 'views/components/utils/TransWithHtml'

import type { LifeStyleProductModalProps } from './LifeStyleProductModalType'

const LifeStyleProductModal: FunctionComponent<LifeStyleProductModalProps> = ({
  id,
  isRx,
  isERx,
  productName,
  productItemTotal,
  productPrice,
  hasBonus,
}: LifeStyleProductModalProps) => {
  const { i18n } = useLingui()
  const RxProductImageSrl = isRx && getPlaceholderImage(true)

  return (
    <Modal
      id={id}
      data-qa-id="life-style-product-modal"
      className="m-LifeStyleProductModal"
      size="large"
      title={<TransWithHtml text={i18n._('productPricing.modal.title')} />}
    >
      <h3 className="m-LifeStyleProductModal__title">
        <Trans id="productPricing.modal.productSection.title" />
      </h3>
      <main className="m-LifeStyleProductModal__content">
        <figure className="m-LifeStyleProductModal__content__thumbnail">
          {isRx && (
            <Image
              className="m-LifeStyleProductModal__content__thumbnail__image"
              src={RxProductImageSrl}
              alt={productName}
              width={80}
              height={80}
            />
          )}
          {isERx && (
            <div className="m-LifeStyleProductModal__content__thumbnail__image">
              <ImageWithDiscountBadge
                isERX
                srcWidth={80}
                srcHeight={80}
                alt={productName}
              />
            </div>
          )}
          <figcaption className="m-LifeStyleProductModal__content__title">
            {productName}
          </figcaption>
        </figure>
        <ul className="m-LifeStyleProductModal__content__list">
          <li className="m-LifeStyleProductModal__content__list-item">
            <Trans id="productPricing.modal.productSection.avp.title" />
            <p className="m-LifeStyleProductModal__content__list-item__text">
              <Price flat amount={productPrice} />
            </p>
          </li>
          <li className="m-LifeStyleProductModal__content__list-item m-LifeStyleProductModal__content__list-item--totalPrice">
            <p className="m-LifeStyleProductModal__content__list-item__text">
              <Trans id="general.label.yourPrice" />
            </p>
            <p className="m-LifeStyleProductModal__content__list-item__text">
              <Price flat amount={productItemTotal} />
            </p>
          </li>
        </ul>
      </main>
      <section className="m-LifeStyleProductModal__notification">
        <Notification
          alignIcon={'top'}
          customBgColor={'white'}
          type={NOTIFICATION_TYPES.INFO_NO_BG_COLOR}
          heading={<Trans id="productPricing.modal.productSection.avp.title" />}
          label={<Trans id="productPricing.modal.productSection.avp.content" />}
        />
        <Notification
          alignIcon={'top'}
          customBgColor={'white'}
          type={NOTIFICATION_TYPES.INFO_NO_BG_COLOR}
          heading={
            <Trans id="productPricing.modal.productSection.ePrescription.title" />
          }
          label={
            <>
              <Trans id="productPricing.modal.productSection.ePrescription.paragraphOne" />
              <br />
              <Trans id="productPricing.modal.productSection.ePrescription.paragraphTwo" />
            </>
          }
        />
      </section>
      {hasBonus && (
        <section className="m-LifeStyleProductModal__bonus">
          <RXBonusContentHeading />
          <RxBonusContent />
        </section>
      )}
    </Modal>
  )
}

export default LifeStyleProductModal
